import React, { useMemo } from 'react';
import {
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Eye,
  Flame,
  Hash,
  MessageSquare,
  Play,
  RefreshCw,
  Sparkles,
  TrendingUp,
  Video,
  Zap,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useHotspot } from '../../context/HotspotContext';
import { HOTSPOT_PLATFORMS } from '../../services/hotspotService';
import CustomSelect from './CustomSelect';

/* ── Constants ── */

const PLATFORM_META = {
  weibo: { label: '微博热搜', color: '#ff8200', bg: 'bg-orange-50 text-orange-700' },
  zhihu: { label: '知乎热榜', color: '#1772f6', bg: 'bg-blue-50 text-blue-700' },
  douyin: { label: '抖音热榜', color: '#111', bg: 'bg-gray-100 text-gray-800' },
  baidu: { label: '百度热搜', color: '#2932e1', bg: 'bg-indigo-50 text-indigo-700' },
  bilibili: { label: 'B站热门', color: '#00a1d6', bg: 'bg-cyan-50 text-cyan-700' },
};

const SORT_OPTIONS = [
  { id: 'heat', label: '综合热度' },
  { id: 'view', label: '浏览量' },
  { id: 'new', label: '最新抓取' },
];

const FORECAST_MAP = {
  rising: { label: '上升', icon: ArrowUpRight, color: 'text-red-500' },
  peak: { label: '高峰', icon: TrendingUp, color: 'text-amber-500' },
  fading: { label: '下降', icon: ArrowDownRight, color: 'text-emerald-500' },
};

const WEIBO_LABEL_STYLES = {
  '爆': 'bg-red-500 text-white',
  '热': 'bg-orange-500 text-white',
  '沸': 'bg-red-600 text-white',
  '新': 'bg-emerald-500 text-white',
  '影': 'bg-purple-500 text-white',
  '音': 'bg-pink-500 text-white',
};

/* ── Helpers ── */

const getPlatformMeta = (id) => PLATFORM_META[id] || { label: id, color: '#999', bg: 'bg-gray-100 text-gray-600' };

const formatHeat = (score) => {
  const n = Number(score || 0);
  if (!Number.isFinite(n) || n <= 0) return '--';
  if (n >= 100000000) return `${(n / 100000000).toFixed(1)}亿`;
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return `${n}`;
};

const formatViewCount = (count) => {
  const n = Number(count || 0);
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}千`;
  return n > 0 ? `${n}` : '--';
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `0:${String(s).padStart(2, '0')}`;
};

const sortHotspots = (items, sortBy) => {
  const copy = [...items];
  switch (sortBy) {
    case 'view':
      return copy.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    case 'new':
      return copy.sort((a, b) => {
        const ta = new Date(a.fetched_at || 0).getTime();
        const tb = new Date(b.fetched_at || 0).getTime();
        return tb - ta;
      });
    case 'heat':
    default:
      return copy.sort((a, b) => (b.heat_score || 0) - (a.heat_score || 0));
  }
};

/* ── ExtraBadges: 平台特色标签 ── */
const ExtraBadges = ({ extra }) => {
  if (!extra) return null;
  const badges = [];

  // 微博标签（爆/新/沸/热）
  if (extra.label) {
    const style = WEIBO_LABEL_STYLES[extra.label] || 'bg-gray-500 text-white';
    badges.push(
      <span key="wb-label" className={`rounded px-1.5 py-0.5 text-[10px] font-black ${style}`}>
        {extra.label}
      </span>
    );
  }

  // 百度置顶
  if (extra.is_top) {
    badges.push(
      <span key="bd-top" className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600 border border-red-100">
        置顶
      </span>
    );
  }

  // B站分区
  if (extra.tname) {
    badges.push(
      <span key="bi-tname" className="inline-flex items-center gap-0.5 rounded bg-cyan-50 px-1.5 py-0.5 text-[10px] font-semibold text-cyan-700">
        <Hash className="h-2.5 w-2.5" />{extra.tname}
      </span>
    );
  }

  return badges.length > 0 ? <>{badges}</> : null;
};

/* ── ExtraMetrics: 平台特有指标 ── */
const ExtraMetrics = ({ extra }) => {
  if (!extra) return null;
  const metrics = [];

  // 作者 (B站/其他)
  if (extra.author) {
    const isBili = extra.tname || extra.danmaku != null || extra.duration != null;
    metrics.push(
      <span key="auth" className="inline-flex items-center gap-1 font-semibold text-gray-500">
        {isBili ? (
          <span className="inline-flex h-[13px] items-center rounded-[3px] border border-gray-300 px-0.5 text-[9px] font-black leading-none text-gray-400">
            UP
          </span>
        ) : (
          <Sparkles className="h-3 w-3" />
        )}
        {extra.author}
      </span>
    );
  }

  // 抖音: 视频数
  if (extra.video_count != null) {
    metrics.push(
      <span key="dy-vc" className="inline-flex items-center gap-1">
        <Video className="h-3 w-3" />{extra.video_count} 视频
      </span>
    );
  }

  // B站: 弹幕
  if (extra.danmaku != null) {
    metrics.push(
      <span key="bi-dm" className="inline-flex items-center gap-1">
        <MessageSquare className="h-3 w-3" />{formatViewCount(extra.danmaku)} 弹幕
      </span>
    );
  }

  // B站: 时长
  const dur = formatDuration(extra.duration);
  if (dur) {
    metrics.push(
      <span key="bi-dur" className="inline-flex items-center gap-1">
        <Play className="h-3 w-3" />{dur}
      </span>
    );
  }

  return metrics.length > 0 ? <>{metrics}</> : null;
};

/* ══════════════════════════════════════════════════════════
   HotspotCard — individual card
   ══════════════════════════════════════════════════════════ */

const HotspotCard = ({ item, rank, platformId, onGenerate }) => {
  const meta = getPlatformMeta(item.source_name?.toLowerCase?.() || platformId);
  const forecast = FORECAST_MAP[item.forecast] || FORECAST_MAP.rising;
  const ForecastIcon = forecast.icon;
  const extra = item.extra || null;
  const hasImage = extra?.image;

  // Extract keywords for tags
  const keywords = Array.isArray(item.keywords)
    ? item.keywords.slice(0, 3)
    : [];

  return (
    <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:border-gray-200">
      {/* ── Top badges ── */}
      <div className="flex flex-wrap items-center gap-1.5 px-4 pt-4 pb-2">
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${meta.bg}`}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
          {meta.label}
        </span>
        <ExtraBadges extra={extra} />
        {keywords.map((kw) => (
          <span
            key={kw}
            className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-600"
          >
            命中: {kw}
          </span>
        ))}
      </div>

      {/* ── Content area: image + title + summary + metrics ── */}
      {hasImage ? (
        /* 有缩略图: 左图右文 layout */
        <div className="flex flex-1 items-start gap-4 px-4 py-1">
          <img
            src={extra.image}
            alt=""
            className="h-20 w-20 flex-shrink-0 rounded-xl object-cover bg-gray-50 shadow-sm border border-black/5"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(e) => { e.target.closest('.flex').querySelector('img').style.display = 'none'; }}
          />
          <div className="flex-1 min-w-0 py-0.5">
            <h4 className="text-[15px] font-bold leading-[1.4] text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
              {item.link ? (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 cursor-pointer"
                  title="打开原始链接"
                >
                  {item.topic || item.title || '未知热点'}
                  <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" />
                </a>
              ) : (
                item.topic || item.title || '未知热点'
              )}
            </h4>

            {/* Sub-metrics below title */}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
              <span className="inline-flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {formatViewCount(item.view_count)}
              </span>
              <ExtraMetrics extra={extra} />
            </div>

            {extra?.desc && (
              <p className="mt-2 text-xs leading-5 text-gray-400 line-clamp-2">{extra.desc}</p>
            )}
          </div>
        </div>
      ) : (
        /* 无缩略图: 标准布局 */
        <div className="flex-1 px-4 py-1">
          <h4 className="text-[15px] font-bold leading-[1.4] text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
            {item.link ? (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 cursor-pointer"
                title="打开原始链接"
              >
                {item.topic || item.title || '未知热点'}
                <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" />
              </a>
            ) : (
              item.topic || item.title || '未知热点'
            )}
          </h4>

          {/* Sub-metrics below title */}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
            <span className="inline-flex items-center gap-1">
              <Eye className="h-3 w-3" />
              {formatViewCount(item.view_count)}
            </span>
            <ExtraMetrics extra={extra} />
          </div>

          <p className="mt-2 text-xs leading-5 text-gray-400 line-clamp-3">
            {item.summary || '点击查看详情'}
          </p>
        </div>
      )}

      {/* ── Bottom metrics ── */}
      <div className="mt-3 flex items-center justify-between border-t border-gray-50 px-4 py-3">
        <div className="flex items-center gap-5">
          {/* Heat */}
          <div className="flex flex-col">
            <span className="text-[10px] font-medium text-gray-400">🔥 热度指标</span>
            <span className="text-[15px] font-black text-red-500 tabular-nums">{formatHeat(item.view_count || item.heat_score)}</span>
          </div>
          {/* Rank */}
          <div className="flex flex-col">
            <span className="text-[10px] font-medium text-gray-400">当前排名</span>
            <span className="text-[15px] font-black text-blue-600 tabular-nums">#{rank}</span>
          </div>
          {/* Trend */}
          <div className="flex flex-col">
            <span className="text-[10px] font-medium text-gray-400">趋势预判</span>
            <div className={`inline-flex items-center gap-0.5 text-[13px] font-bold ${forecast.color}`}>
              <ForecastIcon className="h-3.5 w-3.5" />
              {forecast.label}
            </div>
          </div>
        </div>
      </div>

      {/* ── Generate button ── */}
      <button
        type="button"
        onClick={() => onGenerate?.({ ...item, topic: item.topic || item.title, title: item.topic || item.title })}
        className="flex items-center justify-center gap-1.5 border-t border-gray-100 bg-gray-50/50 px-4 py-3 text-sm font-bold text-gray-500 transition-all hover:bg-[#2563EB] hover:text-white"
      >
        <Sparkles className="h-3.5 w-3.5" />
        立即生成内容卡片
      </button>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   HotspotPage — main
   ══════════════════════════════════════════════════════════ */

const HotspotPage = ({ onGenerateFromHotspot }) => {
  const { user } = useAuth();
  const {
    hotspotsMap,
    loading,
    refreshing,
    selectedPlatforms,
    sortBy,
    cacheInfo,
    setSortBy,
    togglePlatform,
    toggleAll,
    refreshAll,
  } = useHotspot();

  const userCredits = user?.points_balance || 0;

  // Merge all selected platforms into one sorted list
  const mergedHotspots = useMemo(() => {
    const all = [];
    selectedPlatforms.forEach((pid) => {
      const items = hotspotsMap[pid] || [];
      items.forEach((item) => all.push({ ...item, _platform: pid }));
    });
    return sortHotspots(all, sortBy);
  }, [hotspotsMap, selectedPlatforms, sortBy]);

  const isAllSelected = selectedPlatforms.size === HOTSPOT_PLATFORMS.length;
  const lastUpdate = cacheInfo?.cachedAt || '--';


  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#F8FAFC]">
      {/* ── Header ── */}
      <header className="flex-shrink-0 z-20 bg-white/40 backdrop-blur-md border-b border-gray-100">
        <div className="mx-auto w-full max-w-7xl px-4 pt-10 pb-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            {/* Left: Title Area */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="h-10 w-2 bg-[#2563EB] rounded-full shadow-[0_0_15px_rgba(37,99,235,0.4)]" />
                <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-gray-900">
                  今日热点追踪
                </h1>
              </div>
              <p className="text-sm md:text-base font-semibold text-gray-400 max-w-md leading-relaxed">
                实时聚合全网趋势，助你精准捕捉创作灵感，引爆社交媒体。
              </p>
            </div>

            {/* Right: Integrated Action Panel */}
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-white/80 border border-gray-100 rounded-3xl p-1.5 shadow-sm hover:shadow-md transition-all duration-300">
                {/* Last Update */}
                <div className="hidden sm:flex items-center gap-2 px-4 border-r border-gray-100">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest leading-none mb-1">最后更新</span>
                    <span className="text-xs font-bold text-gray-600 flex items-center gap-1.5">
                      <Clock className="h-3 w-3 text-[#2563EB]" />
                      {lastUpdate}
                    </span>
                  </div>
                </div>

                {/* Credits Info */}
                <div className="flex items-center gap-4 px-5 pr-7 border-r border-gray-100 py-1">
                  <div className="flex flex-col items-center">
                    <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest leading-none mb-1">当前余额</span>
                    <span className="text-xs font-black text-gray-700">{userCredits}</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-center gap-1 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-100">
                      <Zap className="h-3 w-3 text-amber-500 fill-amber-500" />
                      <span className="text-[10px] font-black text-amber-600 leading-none">10 积分/次</span>
                    </div>
                  </div>
                </div>

                {/* Refresh Button */}
                <button
                  type="button"
                  onClick={refreshAll}
                  disabled={refreshing}
                  className={`
                    relative group flex h-11 px-5 items-center justify-center gap-2 rounded-2xl transition-all
                    ${refreshing
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#2563EB] to-[#3B82F6] text-white shadow-xl shadow-blue-100 hover:shadow-blue-200 hover:-translate-y-0.5 active:scale-95'
                    }
                  `}
                  title="刷新热点 (消耗 10 积分)"
                >
                  <RefreshCw className={`h-4.5 w-4.5 font-bold ${refreshing ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                  <span className="text-sm font-black tracking-wide">{refreshing ? '刷新中' : '立即更新'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* ── Sub-header: tabs & sort ── */}
          <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between border-t border-gray-100/50 pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={toggleAll}
                  className={`
                    relative h-10 px-6 rounded-2xl text-[13px] font-black transition-all duration-300
                    ${isAllSelected
                      ? 'bg-gray-900 text-white shadow-xl shadow-gray-200 translate-y-[-2px]'
                      : 'bg-white border border-gray-100 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  全部平台
                </button>
                <div className="h-8 w-px bg-gray-100 hidden sm:block" />
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                {HOTSPOT_PLATFORMS.map((p) => {
                  const meta = getPlatformMeta(p.id);
                  const active = selectedPlatforms.has(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePlatform(p.id)}
                      className={`
                        group h-10 flex items-center gap-2.5 rounded-2xl border px-4 text-[13px] font-bold transition-all duration-300
                        ${active
                          ? 'border-transparent bg-white shadow-md text-gray-900 ring-1 ring-black/5 -translate-y-[2px]'
                          : 'border-transparent bg-transparent text-gray-400 hover:text-gray-600 hover:bg-white/50'
                        }
                      `}
                    >
                      <span
                        className={`h-2.5 w-2.5 rounded-full ring-2 ring-white shadow-sm transition-all ${active ? 'scale-110' : 'opacity-40 grayscale scale-90'}`}
                        style={{ backgroundColor: meta.color }}
                      />
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between sm:justify-end gap-6 bg-white/40 sm:bg-transparent p-3 sm:p-0 rounded-2xl border border-gray-50 sm:border-0 px-4 sm:px-0">
              <div className="flex items-center gap-4 group cursor-default">
                <span className="text-[11px] font-black text-gray-300 uppercase tracking-widest group-hover:text-gray-400 transition-colors">排序方式</span>
                <CustomSelect
                  value={sortBy}
                  options={SORT_OPTIONS}
                  onChange={setSortBy}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Card grid ── */}
      <main className="flex-1 overflow-y-auto bg-[#F8FAFC]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex h-[60vh] items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <RefreshCw className="h-8 w-8 animate-spin text-[#2563EB]" />
                <p className="text-sm font-medium text-gray-400">正在同步全网热点...</p>
              </div>
            </div>
          ) : mergedHotspots.length === 0 ? (
            <div className="flex h-[60vh] flex-col items-center justify-center rounded-3xl border border-dashed border-gray-200 bg-white/50 text-gray-400">
              <TrendingUp className="mb-4 h-14 w-14 text-gray-300" />
              <p className="text-lg font-bold text-gray-900">暂无热点数据</p>
              <p className="mt-1 text-sm text-gray-500">点击页面顶部的“刷新”按钮获取最新实时热点</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {mergedHotspots.map((item, index) => (
                <HotspotCard
                  key={item.id || `${item.topic}-${index}`}
                  item={item}
                  rank={index + 1}
                  platformId={item._platform}
                  onGenerate={onGenerateFromHotspot}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default HotspotPage;
